<template>
  <section class="pumping-protection" aria-labelledby="protection-title">
    <div class="protection-heading">
      <div>
        <h1 id="protection-title">Pumping Protection</h1>
        <p class="protection-description">Review outgoing SMS and identify messages that need attention.</p>
      </div>
      <button class="btn protection-secondary" :disabled="loading" @click="load()">Refresh</button>
    </div>
    <div class="protection-status">
      <span class="protection-status-label">
        <mdicon name="shield-check-outline" :size="20" aria-hidden="true" />
        {{ statusLabel }}
      </span>
      <span>Review proposed decisions without blocking or resending messages.</span>
    </div>
    <div v-if="error" role="alert" class="protection-empty">
      <mdicon name="message-text-outline" :size="32" aria-hidden="true" />
      <h2>{{ unavailable ? 'Observation logging is not available yet' : 'Unable to load observations' }}</h2>
      <p>{{ error }}</p>
      <p v-if="unavailable" class="protection-caption">SMS sending is unchanged. Observations will appear here once logging is connected.</p>
    </div>
    <BCard v-if="result?.volume_model" class="mb-4">
      <h2 class="h4">Volume follows shipments</h2>
      <p>Expected SMS = historical SMS per shipment × current shipment volume × a safety margin.</p>
      <p>Calculated globally and per customer using booked proxy shipments. The last seven days also provide allowance for delayed notifications.</p>
      <p v-if="result.volume_model.status === 'ready'" class="mb-0">
        Reviewed baseline: {{ result.volume_model.baseline_from }}–{{ result.volume_model.baseline_to }} (UTC).
        Current global allowance: {{ result.volume_model.limits.global_day }} SMS / 24 hours. This grows with shipment volume.
      </p>
      <p v-else class="mb-0">
        {{ result.volume_model.status === 'data_unavailable' ? 'Shipment or baseline data is unavailable.' : 'Volume comparison awaits a reviewed baseline with sufficient history.' }}
        No fixed business-volume ceiling is substituted. Duplicate, per-recipient and per-message checks remain separate.
      </p>
    </BCard>
    <TwilioComparison />
    <BCard class="filter protection-filters">
      <h3>Filter observations</h3>
      <form class="protection-filter-grid" @submit.prevent="load(1)">
        <label>From (UTC)<input v-model="from" type="date" class="form-control" /></label>
        <label>To (UTC)<input v-model="to" type="date" class="form-control" /></label>
        <label>Decision
          <select v-model="decision" class="form-select">
            <option value="">All decisions</option>
            <option value="allow">Would allow</option>
            <option value="would_pause">Would pause for review</option>
            <option value="would_block">Would block</option>
          </select>
        </label>
        <label>Shipment<input v-model="shipment" class="form-control" maxlength="100" /></label>
        <button class="btn btn-green" :disabled="loading">Apply filters</button>
      </form>
    </BCard>
    <p v-if="loading" role="status">Loading observations…</p>
    <template v-if="result">
      <p class="protection-caption">
        Last recorded attempt (UTC): {{ result.last_observation_at || 'None' }}.
        Missing observations and unknown provider outcomes require investigation before enabling blocking.
      </p>
      <div class="row g-3 mb-3">
        <div v-for="summary in result.summary" :key="summary.decision" class="col-md-4">
          <div class="card protection-metric">
            <h2 class="h6">{{ labels[summary.decision] }}</h2>
            <strong>{{ summary.attempts }} attempts</strong>
            <span>{{ money(summary.estimated_cost_usd) }} estimated traffic value</span>
            <small>{{ summary.unpriced_attempts }} attempts without a known tariff</small>
          </div>
        </div>
      </div>
      <p>Estimated traffic value is not money saved. Messages still go to Twilio; its fees and decisions still apply.</p>
      <details class="card protection-rules">
        <summary>Provisional rules · {{ result.rules.version }}</summary>
        <p class="mt-2">Limits use rolling windows. Duplicate window: {{ result.rules.duplicate_seconds }} seconds.</p>
        <dl class="protection-rule-list">
          <div v-for="(limit, rule) in result.rules.limits" :key="rule">
            <dt>{{ limitLabels[rule] || rule.replaceAll('_', ' ') }}</dt><dd>{{ limit }}</dd>
          </div>
          <div><dt>Allowed countries</dt><dd>{{ result.rules.allowed_countries.join(', ') || 'All destinations' }}</dd></div>
        </dl>
      </details>
      <div class="table-responsive protection-table" :aria-busy="loading">
        <table class="table align-middle">
          <caption>Outgoing SMS observations · {{ result.from }}–{{ result.to }} (UTC)</caption>
          <thead>
            <tr>
              <th>Time / shipment</th><th>Destination</th><th>Proposed decision</th>
              <th>Provider result</th><th>Estimated cost</th><th>Review</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in result.observations.data" :key="row.id">
              <td>{{ row.created_at }}<br /><strong>{{ row.shipment_number || 'No shipment context' }}</strong></td>
              <td>{{ row.recipient_mask }}<br />{{ row.country || 'Unknown country' }}</td>
              <td>
                <strong>{{ labels[row.decision] }}</strong>
                <ul class="mb-1"><li v-for="reason in row.reasons" :key="reason">{{ protectionReasonLabel(reason, row.evidence) }}</li></ul>
                <details><summary>Decision evidence</summary><pre>{{ JSON.stringify(row.evidence, null, 2) }}</pre></details>
              </td>
              <td>
                {{ row.provider_status === 'accepted' ? 'Accepted by Twilio (delivery unconfirmed)' : row.provider_status }}
                <br v-if="row.provider_error_code" />{{ row.provider_error_code || '' }}
              </td>
              <td>{{ money(row.estimated_cost_usd) }}<br />{{ row.segments }} estimated segments</td>
              <td>
                <span v-if="row.reviews[0]">{{ verdictLabels[row.reviews[0].verdict] }}</span>
                <button class="btn btn-sm protection-secondary d-block" @click="select(row)">Review attempt</button>
              </td>
            </tr>
            <tr v-if="!result.observations.data.length"><td colspan="6">No observations match these filters.</td></tr>
          </tbody>
        </table>
      </div>
      <nav aria-label="Observation pages" class="d-flex gap-3 align-items-center">
        <button
          class="btn protection-secondary" :disabled="loading || result.observations.current_page <= 1"
          @click="load(result.observations.current_page - 1)"
        >
          Previous
        </button>
        <span>Page {{ result.observations.current_page }} / {{ result.observations.last_page }}</span>
        <button
          class="btn protection-secondary"
          :disabled="loading || result.observations.current_page >= result.observations.last_page"
          @click="load(result.observations.current_page + 1)"
        >
          Next
        </button>
      </nav>
    </template>
    <section v-if="selected" class="card protection-review" aria-labelledby="review-title">
      <h2 id="review-title" class="h4">Review attempt #{{ selected.id }}</h2>
      <p>Record whether the proposed decision was appropriate. Saving a review does not send or release an SMS.</p>
      <ul>
        <li v-for="review in selected.reviews" :key="review.id">
          {{ review.created_at }} · {{ verdictLabels[review.verdict] }} · {{ review.comment }}
        </li>
      </ul>
      <form @submit.prevent="saveReview">
        <label class="d-block mb-3">Assessment
          <select v-model="verdict" class="form-select">
            <option value="uncertain">Needs investigation</option>
            <option value="correct">Proposed decision is correct</option>
            <option value="false_positive">Too aggressive — legitimate SMS</option>
          </select>
        </label>
        <label class="d-block mb-3">Reason (do not include phone numbers or message text)
          <textarea v-model="comment" class="form-control" required maxlength="1000" rows="3" />
        </label>
        <button class="btn btn-green" :disabled="saving || !comment.trim()">Save assessment</button>
        <button type="button" class="btn protection-secondary ml-2" :disabled="saving" @click="selected = null">Close</button>
        <p v-if="reviewError" role="alert" class="text-danger mt-2">{{ reviewError }}</p>
      </form>
    </section>
    <p v-if="saved" role="status" class="protection-status mt-3">Assessment saved. Sending behaviour is unchanged.</p>
  </section>
</template>

<script setup lang="ts">
import type { ProtectionDecision, ProtectionObservation, ProtectionResponse, ProtectionVerdict } from '@/types/Models/SmsProtection'
import { isAxiosError } from 'axios'
import { BCard } from 'bootstrap-vue-next'
import { computed, onMounted, ref } from 'vue'
import { protectionReasonLabel } from '@/utils/smsProtection'
import Axios from '@/services/Axios'
import TwilioComparison from '@/views/Staff/PumpingProtection/TwilioComparison.vue'

const result = ref<ProtectionResponse | null>(null)
const loading = ref(false)
const error = ref('')
const unavailable = ref(false)
const statusLabel = computed(() => {
  if (loading.value) return 'Checking status…'
  if (result.value?.mode === 'observe') return 'Observation mode'
  return result.value?.mode === 'disabled' ? 'Observation inactive' : 'Awaiting observations'
})
const from = ref('')
const to = ref('')
const decision = ref<ProtectionDecision | ''>('')
const shipment = ref('')
const selected = ref<ProtectionObservation | null>(null)
const verdict = ref<ProtectionVerdict>('uncertain')
const comment = ref('')
const saving = ref(false)
const saved = ref(false)
const reviewError = ref('')
const labels = { allow: 'Would allow', would_pause: 'Would pause for review', would_block: 'Would block' }
const verdictLabels = { correct: 'Correct', false_positive: 'Too aggressive', uncertain: 'Needs investigation' }
const limitLabels: Record<string, string> = {
  global_minute: 'All SMS / minute', company_minute: 'Per customer / minute',
  recipient_minute: 'Per recipient / minute', country_hour: 'Per country / hour',
  global_day: 'All SMS / 24 hours', global_day_usd: 'Estimated spend / 24 hours (USD)',
  company_day_usd: 'Customer spend / 24 hours (USD)', max_segments: 'Segments per message',
}
const money = (value: string | null) => value === null ? 'Unknown cost' : `${Number(value).toFixed(4)} USD`

async function load(page = 1) {
  if (loading.value) return
  loading.value = true
  error.value = ''
  unavailable.value = false
  try {
    const response = await Axios.get<ProtectionResponse>('/sms-protection', {
      params: { page, from: from.value || undefined, to: to.value || undefined,
        decision: decision.value || undefined, shipment_number: shipment.value || undefined },
    })
    result.value = response.data
    from.value = response.data.from
    to.value = response.data.to
  } catch (cause) {
    result.value = null
    unavailable.value = isAxiosError(cause) && [404, 503].includes(cause.response?.status || 0)
    error.value = unavailable.value
      ? 'Observation logging is not available in this environment yet.'
      : 'Could not load protection observations. Check your access and try again.'
  } finally {
    loading.value = false
  }
}

function select(row: ProtectionObservation) {
  selected.value = row
  verdict.value = 'uncertain'
  comment.value = ''
  reviewError.value = ''
  saved.value = false
}

async function saveReview() {
  if (!selected.value || saving.value || !comment.value.trim()) return
  saving.value = true
  reviewError.value = ''
  try {
    await Axios.post(`/sms-protection/${selected.value.id}/reviews`, { verdict: verdict.value, comment: comment.value })
    selected.value = null
    saved.value = true
    await load(result.value?.observations.current_page || 1)
  } catch {
    reviewError.value = 'Assessment could not be saved. Your draft has been kept.'
  } finally {
    saving.value = false
  }
}

onMounted(() => load())
</script>

<style scoped lang="scss">
.pumping-protection {
  padding-bottom: 40px;
  color: variables.$black;
}
.protection-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  h1 { margin-bottom: 4px; color: variables.$dark-green; }
}
.protection-description, .protection-caption {
  color: variables.$gray-color;
  margin-bottom: 0;
}
.protection-caption { margin: 16px 0; font-size: 14px; }
.protection-status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 16px 24px;
  margin-bottom: 24px;
  background: variables.$light-gray-bg;
  border: 1px solid variables.$beige;
  border-radius: variables.$border-radius;
  font-size: 14px;
}
.protection-status-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: variables.$dark-green;
  font-weight: 500;
}
.protection-empty {
  padding: 40px 24px;
  margin-bottom: 24px;
  background: variables.$white;
  border: 1px solid variables.$light-border;
  border-radius: variables.$border-radius;
  text-align: center;
  > .mdi { color: variables.$green; }
  h2 { margin: 16px 0 8px; font-size: 20px; color: variables.$dark-green; }
  p { margin-bottom: 8px; }
}
.protection-filters {
  margin-bottom: 24px;
  h3 { margin-bottom: 24px; }
}
.protection-filter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(140px, 1fr)) minmax(200px, 1.3fr) minmax(180px, 1.3fr) auto;
  gap: 16px;
  align-items: end;
  label { display: grid; gap: 8px; font-size: 14px; margin: 0; }
  > .btn { height: 45px; }
}
.form-select {
  min-height: 45px;
  border-color: variables.$beige;
  border-radius: variables.$border-radius;
  color: variables.$black;
}
.btn.protection-secondary {
  color: variables.$dark-green;
  background: variables.$white;
  border: 1px solid variables.$beige;
  white-space: nowrap;
  &:hover:not(:disabled) { background: variables.$light-gray-bg; border-color: variables.$gray-hover; }
  &:disabled { color: variables.$gray-color; }
}
.protection-metric {
  height: 100%;
  padding: 24px;
  border-color: variables.$light-border;
  strong { color: variables.$dark-green; font-size: 24px; margin: 8px 0; }
  small { color: variables.$gray-color; margin-top: 8px; }
}
.protection-rules {
  padding: 20px 24px;
  margin: 24px 0;
  summary { color: variables.$dark-green; cursor: pointer; }
}
.protection-rule-list {
  margin: 16px 0 0;
  > div { display: flex; justify-content: space-between; gap: 24px; padding: 10px 0; border-top: 1px solid variables.$light-border; }
  dt { font-weight: 400; }
  dd { margin: 0; font-weight: 500; }
}
.protection-table {
  background: variables.$white;
  border: 1px solid variables.$light-border;
  border-radius: variables.$border-radius;
  margin: 24px 0;
  table { margin-bottom: 0; }
  th { background: variables.$dark-green; color: variables.$white; font-weight: 500; }
  th, td { padding: 16px; vertical-align: top; }
  tbody tr:nth-child(even) td { background: variables.$light-gray-bg; }
  caption { padding: 12px 16px; color: variables.$gray-color; }
  summary { color: variables.$green; cursor: pointer; }
}
.protection-review { padding: 32px; margin-top: 24px; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; max-width: 38rem; font-size: 13px; }
:where(button, input, select, textarea, summary):focus-visible {
  outline: 2px solid variables.$green-hover !important;
  outline-offset: 3px;
}
@media (max-width: 1100px) {
  .protection-filter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 600px) {
  .protection-heading { align-items: flex-start; flex-direction: column; }
  .protection-filter-grid { grid-template-columns: 1fr; }
  .protection-review { padding: 20px; }
}
</style>
